import { Container, Graphics, Text, TextStyle, Rectangle, InteractionEvent } from "pixi.js";
import Layout from "../../../../vendor_core/core/src/utils/Layout";

export interface IRulesArea {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface IRulesParagraph {
    text: string;
    bold?: boolean;
    center?: boolean;
}

/** All values are in PSD pixels and go through Layout.scalePixels */
export interface IRulesStyle {
    fontSize: number;
    lineHeight: number;
    paragraphGap: number;
    scrollbarWidth: number;
    scrollbarGutter: number;
}

const WHEEL_VIEWS = new Set<BURRulesScrollView>();
document.addEventListener("wheel", (e: WheelEvent) => {
    WHEEL_VIEWS.forEach((view) => view.onWheel(e.deltaY));
});

/**
 * Body of the feature rules / game rules pages. Ignores the PSD text styles: every page renders its paragraphs with
 * the same font, size and spacing, wraps them to the area width and scrolls vertically when they do not fit.
 */
export default class BURRulesScrollView extends Container {
    private content: Container = new Container();
    private scrollbar: Graphics = new Graphics();
    private thumb: Graphics = new Graphics();
    private scrollbarWidth: number = 0;
    private areaWidth: number;
    private areaHeight: number;
    private contentHeight: number = 0;
    private dragStartY: number = 0;
    private dragStartContentY: number = 0;
    private dragging: boolean = false;

    constructor(paragraphs: IRulesParagraph[], area: IRulesArea, style: IRulesStyle, fill: string | number = "#FFFFFF") {
        super();

        this.x = Layout.scalePixels(area.x);
        this.y = Layout.scalePixels(area.y);
        this.areaWidth = Layout.scalePixels(area.width);
        this.areaHeight = Layout.scalePixels(area.height);

        const textWidth = this.areaWidth - Layout.scalePixels(style.scrollbarGutter);
        const gap = Layout.scalePixels(style.paragraphGap);
        let y = 0;
        paragraphs.forEach(({ text, bold, center }) => {
            const field = new Text(text, new TextStyle({
                fontFamily: bold ? "notosans_bold" : "notosans",
                fontSize: Layout.scalePixels(style.fontSize),
                lineHeight: Layout.scalePixels(style.lineHeight),
                fill,
                align: center ? "center" : "left",
                wordWrap: true,
                wordWrapWidth: textWidth,
                breakWords: true
            }));
            field.y = y;
            if (center) {
                field.x = Math.max(0, (textWidth - field.width) / 2);
            }
            this.content.addChild(field);
            y += field.height + gap;
        });
        this.contentHeight = Math.max(0, y - gap);

        const mask = new Graphics();
        mask.beginFill(0xffffff, 1);
        mask.drawRect(0, 0, this.areaWidth, this.areaHeight);
        mask.endFill();
        this.addChild(mask);
        this.addChild(this.content);
        this.content.mask = mask;

        if (this.isScrollable) {
            this.scrollbarWidth = Layout.scalePixels(style.scrollbarWidth);
            this.scrollbar.x = this.areaWidth - this.scrollbarWidth;
            this.addChild(this.scrollbar);
            this.drawScrollbar();

            // only interactive when it scrolls, so it never covers the buttons of short pages
            this.interactive = true;
            this.hitArea = new Rectangle(0, 0, this.areaWidth, this.areaHeight);
            this.on("pointerdown", this.onDragStart, this);
            this.on("pointermove", this.onDragMove, this);
            this.on("pointerup", this.onDragEnd, this);
            this.on("pointerupoutside", this.onDragEnd, this);
            WHEEL_VIEWS.add(this);
            this.once("destroyed", () => WHEEL_VIEWS.delete(this));
        }
    }

    public get isScrollable(): boolean {
        return this.contentHeight > this.areaHeight;
    }

    private get minY(): number {
        return Math.min(0, this.areaHeight - this.contentHeight);
    }

    private scrollTo(y: number): void {
        this.content.y = Math.max(this.minY, Math.min(0, y));
        this.moveThumb();
    }

    private get thumbHeight(): number {
        return Math.max(
            this.areaHeight * (this.areaHeight / this.contentHeight),
            this.scrollbarWidth * 3
        );
    }

    /** The track and the thumb are drawn once, scrolling only moves the thumb */
    private drawScrollbar(): void {
        this.scrollbar.beginFill(0xffffff, 0.3);
        this.scrollbar.drawRect(0, 0, this.scrollbarWidth, this.areaHeight);
        this.scrollbar.endFill();
        this.thumb.beginFill(0xffffff, 0.9);
        this.thumb.drawRect(0, 0, this.scrollbarWidth, this.thumbHeight);
        this.thumb.endFill();
        this.scrollbar.addChild(this.thumb);
        this.moveThumb();
    }

    private moveThumb(): void {
        const progress = this.minY === 0 ? 0 : this.content.y / this.minY;
        this.thumb.y = progress * (this.areaHeight - this.thumbHeight);
    }

    public onWheel(deltaY: number): void {
        if (!this.worldVisible) {
            return;
        }
        this.scrollTo(this.content.y - deltaY);
    }

    private onDragStart(e: InteractionEvent): void {
        this.dragging = true;
        this.dragStartY = e.data.getLocalPosition(this).y;
        this.dragStartContentY = this.content.y;
    }

    private onDragMove(e: InteractionEvent): void {
        if (this.dragging) {
            this.scrollTo(this.dragStartContentY + e.data.getLocalPosition(this).y - this.dragStartY);
        }
    }

    private onDragEnd(): void {
        this.dragging = false;
    }
}
